/**
 * DEC-346 (a) / OPEN-131 — this company's requests for people who already have a lodeX Pro
 * login at another company. An import no longer adds such a person directly. It asks them,
 * and they join only when they accept in their app.
 *
 * MANAGE_USERS and own-tenant-only server-side (GET /v1/link-requests and its cancel and
 * resend routes). An accept happens on the driver's phone, never here, so the list is
 * re-read on an interval: a request that turns ACCEPTED also adds a person to the Team
 * and Fleet lists, which share the ['users'] and ['drivers'] prefixes invalidated below.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

export const LINK_REQUESTS_QUERY_KEY = ['users', 'link-requests'] as const;

/** EXPIRED is computed by the API from `expiresAt`; it is never stored as PENDING-past-due. */
export type LinkRequestStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED' | 'EXPIRED';

export interface LinkRequest {
  readonly id: string;
  readonly userId: string;
  readonly fullName: string;
  /** Only the last four digits: the full number belongs to the other company's record. */
  readonly phoneLast4: string | null;
  readonly role: string;
  readonly status: LinkRequestStatus;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly decidedAt: string | null;
  readonly importBatchId: string | null;
}

export function useLinkRequests(): ReturnType<
  typeof useApiQuery<{ requests: readonly LinkRequest[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ requests: readonly LinkRequest[] }>(opts, '/v1/link-requests', {
    queryKey: [...LINK_REQUESTS_QUERY_KEY],
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

function useInvalidateLinkSurfaces(): () => void {
  const qc = useQueryClient();
  return () => {
    // ['users'] covers both the team list and this list.
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['drivers'] });
  };
}

/** Withdraw an open request. Always sends `{}`: a body-less POST is a 400 (DEC-201). */
export function useCancelLinkRequest(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { id: string; status: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateLinkSurfaces();
  // Invalidate on error too: a 404 means it was answered or withdrawn meanwhile.
  return useApiMutation<Record<string, never>, { id: string; status: string }>(
    opts,
    `/v1/link-requests/${id}/cancel`,
    'POST',
    { onSettled: () => invalidate() },
  );
}

/** Ask again after a request EXPIRED (D5). The API refuses anything else with a 400. */
export function useResendLinkRequest(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { id: string; status: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateLinkSurfaces();
  return useApiMutation<Record<string, never>, { id: string; status: string }>(
    opts,
    `/v1/link-requests/${id}/resend`,
    'POST',
    { onSettled: () => invalidate() },
  );
}
